import React, { useContext, useState, useEffect } from "react";
import { AccountContext } from "@context/AccountProvider";
import { useTranslation } from "react-i18next";
import { useShopItemListQuery } from "@query/shopQuery";
import { shopItemListFilteringEnum } from "@service/shopItemService";

// Material UI Components
import Box from "@mui/material/Box";
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";
import AddIcon from '@mui/icons-material/Add';
import { Typography } from "@mui/material";

// Custom Components
import ShopItemCard from "@component/ShopItemCard";
import ShopPageFiltering from "@component/ShopPageFiltering";
import SidePanel from "@component/SidePanel";
import ShopItemSidePanelPage from "@component/ShopItemSidePanelPage";
import CreateShopItemForm from "@component/ShopItemCreateForm";

export default function ShopPage() {
  const { club } = useContext(AccountContext);
  const { t } = useTranslation(["main", "shop"]);

  const [queryParams, setQueryParams] = useState({
    page: 0,
    clubUuid: club.uuid,
    name: null,
    siteUuid: null,
    availibilityFilter: shopItemListFilteringEnum.ONLY_AVAILABLE,
  });

  const [shopItems, setShopItems] = useState(null);

  // State for detail/update panel
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [selectedItemUuid, setSelectedItemUuid] = useState(null);

  // State for create panel
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const [createFormKey, setCreateFormKey] = useState(Date.now());

  const shopItemListQuery = useShopItemListQuery(queryParams);

  function handleAfterShopItemCreate(shopItemUuid) {
    setCreatePanelOpen(false)
    setDetailPanelOpen(true)
    setSelectedItemUuid(shopItemUuid)
  }

  useEffect(() => {
    if (shopItemListQuery.data?.data) {
      setShopItems(shopItemListQuery.data.data);
    }
  }, [shopItemListQuery.data]);

  // --- Handlers for filtering and pagination ---
  function onSearchChange(newText) {
    const text = newText.length === 0 ? null : newText;
    setQueryParams((prev) => ({ ...prev, name: text, page: 0 }));
  }

  function onPaginationChange(event, value) {
    setQueryParams((prev) => ({ ...prev, page: value }));
  }

  function onFilterChange(newValue) {
    setQueryParams((prev) => ({ ...prev, availibilityFilter: newValue, page: 0 }));
  }

  function onSiteChange(newValue) {
    setQueryParams((prev) => ({ ...prev, siteUuid: newValue, page: 0 }));
  }

  // --- Handlers for side panels ---
  function handleDetailPanelClose() {
    setDetailPanelOpen(false);
    setSelectedItemUuid(null);
    shopItemListQuery.refetch();
  }

  function handleCardClick(uuid) {
    setSelectedItemUuid(uuid);
    setDetailPanelOpen(true);
  }

  function handleCreatePanelClose() {
    setCreatePanelOpen(false);
    setCreateFormKey(Date.now());
  }

  const isLoading = shopItemListQuery.isLoading;

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "80%", md: "80%" },
        display: "flex",
        flexDirection: "column",
        gap: 3,
        mx: "auto",
        pb: 4,
      }}
    >
      {/* Side panel for VIEWING/UPDATING an existing item */}
      <SidePanel
        open={detailPanelOpen}
        closeOnOutsideClick={true}
        onClose={handleDetailPanelClose}
        title={t("shop:product")}
      >
        <ShopItemSidePanelPage shopItemUuid={selectedItemUuid} />
      </SidePanel>

      {/* Side panel for CREATING a new item */}
      <SidePanel
        open={createPanelOpen}
        closeOnOutsideClick={false}
        onClose={handleCreatePanelClose}
        title={t("shop:createNewShopItem")}
      >
        <CreateShopItemForm key={createFormKey} onSuccess={handleAfterShopItemCreate} />
      </SidePanel>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flex: "1", my: 2, mt: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h2" mb={1}>
            {t('shop:pageTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('shop:pageSubtitle')}
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          color="primary"
          variant="contained"
          onClick={() => setCreatePanelOpen(true)}
        >
          {t("shop:createNewShopItem")}
        </Button>
      </Box>

      <ShopPageFiltering
        searchText={queryParams.name}
        onSearchChange={onSearchChange}
        selectedFilter={queryParams.availibilityFilter}
        onFilterChange={onFilterChange}
        site={queryParams.site}
        onSiteChange={onSiteChange}
      />

      <Grid container spacing={1} alignItems="stretch">
        {isLoading
          ? Array.from({ length: 8 }).map((_, index) => (
            <Grid item key={index} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
              <Skeleton variant="rectangular" height={200} />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="60%" />
            </Grid>
          ))
          : shopItems?.items?.map((item) => (
            <Grid
              item
              key={`sic_${item.uuid}`}
              onClick={() => handleCardClick(item.uuid)}
              size={{ xs: 12, sm: 6, md: 6, lg: 4 }}
            >
              <ShopItemCard item={item} />
            </Grid>
          ))}
      </Grid>

      <Stack
        spacing={2}
        sx={{ display: "flex", width: "100%", alignItems: "center" }}
      >
        {shopItems && shopItems.pagination.total_pages > 1 ? (
          <Pagination
            count={shopItems.pagination.total_pages}
            page={(shopItems.pagination.current_page || 0) + 1}
            onChange={(event, page) => onPaginationChange(event, page - 1)}
            size="medium"
            showFirstButton
            showLastButton
          />
        ) : null}
      </Stack>
    </Box>
  );
}